import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useSelector } from "react-redux";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import { PiInteractionDropdown } from "../../../components/PiInteractionDropdown";
import { selectCurrentLanguage } from "../../../app/slices/TrainingLocalizationSlice";
import {
  selectActors,
  selectSequenceVariables,
} from "../../../app/slices/TrainingSlice";
import { useScreenScale } from "../../../hooks/ScreenScale.hook";
import {
  IMessageBubble,
  messageBubbleStyles,
} from "../../../models/interactions/MessageBubble";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import RichTextEditor from "../../rich_text/RichTextEditor";
import "./MessageBubblePreviewText.css";

type MessageBubblePreviewTextVariant = "standalone" | "embedded";

export default function MessageBubblePreviewText(props: {
  messageBubble: IMessageBubble;
  updateInteraction: (field: string, value: any) => void;
  variant?: MessageBubblePreviewTextVariant;
}) {
  const { messageBubble, updateInteraction, variant = "embedded" } = { ...props };
  const isStandalone = variant === "standalone";
  const currentActor = useSelector(selectActors).find(
    (a) => a.id === messageBubble.npcId
  );
  const variables = useSelector(selectSequenceVariables);
  const currentLang = useSelector(selectCurrentLanguage);
  const { getValue, changeValue } = useTrainingLocalization();
  const { scale } = useScreenScale();
  const localizedValue = getValue(messageBubble.text);

  const messageBubbleStyleDropdownItems = messageBubbleStyles.map((style) => (
    <PiInteractionDropdown.Item
      key={style}
      onClick={() => updateInteraction("messageBubbleStyle", style)}
    >
      <div className={"flex w-full"}>
        <div className={"my-auto truncate"}>{style}</div>
      </div>
    </PiInteractionDropdown.Item>
  ));

  const messageBubbleStyleDropdown = (
    <PiInteractionDropdown
      className="messagebubblepreviewtext-styleDropdown"
      triggerClassName="messagebubblepreviewtext-styleDropdownTrigger"
      contentClassName="messagebubblepreviewtext-styleDropdownContent"
    >
      <PiInteractionDropdown.Trigger>
        <div className={"flex w-full min-w-0"}>
          <div className={"my-auto truncate"}>{messageBubble.messageBubbleStyle}</div>
        </div>
      </PiInteractionDropdown.Trigger>
      {messageBubbleStyleDropdownItems}
    </PiInteractionDropdown>
  );

  const avatar = (
    <div className={"messagebubblepreviewtext-avatar"}>
      <ActorAvatar actor={currentActor} />
    </div>
  );

  const textarea = (
    <div
      style={isStandalone ? undefined : { transform: `scale(${scale})` }}
      className={`messagebubblepreviewtext-scaledScene ${
        isStandalone
          ? "messagebubblepreviewtext-scaledScene--standalone"
          : "messagebubblepreviewtext-scaledScene--embedded"
      }`}
    >
      <div
        className={`messagebubblepreviewtext-bubbleCard ${
          isStandalone
            ? "messagebubblepreviewtext-bubbleCard--standalone"
            : "messagebubblepreviewtext-bubbleCard--embedded"
        }`}
      >
        <div className={"messagebubblepreviewtext-speakerName"}>
          {currentActor?.workingName ?? "No Actor"}
        </div>
        <RichTextEditor
          ariaLabel="messagebubble-text-area"
          value={localizedValue}
          onCommit={(nextValue) =>
            changeValue(messageBubble.text, nextValue, currentLang.shortcode)
          }
          variables={variables}
          debounceMs={500}
          flushOnCleanupKeys={[currentLang.shortcode, messageBubble.id]}
          placeholder="Message"
          contentClassName="messagebubblepreviewtext-richtext pit-scrollbar"
          warningClassName="messagebubblepreviewtext-warning"
        />
      </div>
      <div
        className={`messagebubblepreviewtext-arrowWrap ${
          isStandalone
            ? "messagebubblepreviewtext-arrowWrap--standalone"
            : "messagebubblepreviewtext-arrowWrap--embedded"
        }`}
      >
        <ArrowDropDownIcon className={"text-2xl"} fontSize={"inherit"} />
      </div>
      {avatar}
      <div
        className={`messagebubblepreviewtext-styleCycleWrap ${
          isStandalone
            ? "messagebubblepreviewtext-styleCycleWrap--standalone"
            : "messagebubblepreviewtext-styleCycleWrap--embedded"
        }`}
      >
        {messageBubbleStyleDropdown}
      </div>
    </div>
  );

  return (
    <div className="messagebubblepreviewtext-root">
      <div
        className={`messagebubblepreviewtext-canvas ${
          isStandalone
            ? "messagebubblepreviewtext-canvas--standalone"
            : "messagebubblepreviewtext-canvas--embedded"
        }`}
      >
        <div className={"messagebubblepreviewtext-scene"}>{textarea}</div>
      </div>
    </div>
  );
}
